import React, { useState, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  AppBar,
  Drawer,
  Toolbar,
  Divider,
  List,
  ListItem,
  Icon,
  Stack,
  ListSubheader,
  IconButton,
  Tooltip,
  Menu,
  MenuItem
} from 'investira.react.components';
import { validators } from 'investira.sdk';
import { UserProfile } from '../../../components/molecules';
import { SECTIONS, OUTPUT } from '../../../enums';
import usePerfil from '../../../hooks/usePerfil';
import useAcesso from '../../../hooks/useAcesso';

const MAIN_ROUTES_WIDTH = 64;

const Sidebar = props => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { usuarioPrioridade } = usePerfil();
  const { canAcessar } = useAcesso();

  const [anchorEl, setAnchorEl] = useState(null);
  const [activeItem, setActiveItem] = useState(null);

  const timerRef = useRef(null);
  const submenuRef = useRef(null);

  const open = Boolean(anchorEl);

  const handleItemHover = (pEvent, pItem) => {
    if (pItem.nested) {
      clearTimeout(timerRef.current);
      setAnchorEl(pEvent.currentTarget);
      setActiveItem(pItem);
    }
  };

  const isMouseOverSubmenu = () => {
    return (
      submenuRef.current?.contains(document.activeElement) || submenuRef.current?.matches(':hover')
    );
  };

  const isMouseOverItem = pEvent => {
    if (!(pEvent.relatedTarget instanceof Node)) {
      return null;
    }
    return (
      pEvent.currentTarget?.contains(pEvent.relatedTarget) ||
      pEvent.currentTarget?.matches(':hover')
    );
  };

  const requestClose = () => {
    timerRef.current = setTimeout(() => {
      if (!isMouseOverSubmenu()) {
        setAnchorEl(null);
        setActiveItem(null);
      }
    }, 100);
  };

  const handleItemLeave = pEvent => {
    // Só fecha se o mouse não estiver indo para outro item ou submenu
    if (!isMouseOverItem(pEvent) && !isMouseOverSubmenu()) {
      requestClose();
    }
  };

  const handleSubmenuEnter = () => {
    clearTimeout(timerRef.current);
  };

  const handleSubmenuLeave = () => {
    requestClose();
  };

  const options = [
    {
      ...SECTIONS.dashboard,
      nested: null,
      disabled: false
    },
    {
      key: 'divider'
    },
    {
      ...SECTIONS.reports,
      disabled: false,
      nested: [
        {
          ...SECTIONS.duration,
          disabled: !canAcessar(OUTPUT.duration.output_id),
          beta: false
        },
        {
          ...SECTIONS.enquadramento,
          disabled: !canAcessar(OUTPUT.enquadramento.output_id),
          beta: false
        },
        {
          ...SECTIONS.var,
          disabled: !canAcessar(OUTPUT.var.output_id),
          beta: true
        },
        {
          ...SECTIONS.stresstest,
          disabled: false,
          beta: true
        },
        {
          ...SECTIONS.conferenciacustodiante,
          disabled: !canAcessar(OUTPUT.conferenciacustodiante.output_id),
          beta: false
        },
        {
          ...SECTIONS.rentabilidade,
          disabled: true,
          beta: false
        },
        {
          ...SECTIONS.raiox,
          disabled: true,
          beta: false
        },
        {
          ...SECTIONS.backtesting,
          disabled: true,
          beta: false
        },
        {
          ...SECTIONS.fronteiraeficiente,
          disabled: true,
          beta: false
        }
      ]
    },
    {
      ...SECTIONS.portfolios,
      nested: null,
      disabled: process.env.REACT_APP_ENV === 'prd'
    },
    {
      ...SECTIONS.carteiras,
      nested: null,
      disabled: false
    },
    {
      ...SECTIONS.uploads,
      nested: null,
      disabled: usuarioPrioridade > 6
    },
    {
      key: 'divider',
      component: <Divider />
    },
    {
      ...SECTIONS.empresas,
      nested: null,
      disabled: false
    },
    {
      ...SECTIONS.ativos,
      nested: null,
      disabled: false
    },
    {
      ...SECTIONS.calculadoras,
      nested: [
        {
          key: 'variacao',
          label: 'Cálculo de Variação',
          path: `/calculadoras/variacao`,
          nested: null,
          disabled: false
        },
        {
          key: 'curva',
          label: 'Cálculo de Curva',
          path: `/calculadoras/curva`,
          nested: null,
          disabled: false
        }
      ],
      disabled: false
    },
    {
      ...SECTIONS.indicadores,
      nested: null,
      disabled: false
    },
    {
      ...SECTIONS.correlacao,
      // nested: [
      //   {
      //     ...SECTIONS.pares,
      //     nested: null,
      //     disabled: false,
      //   },
      //   {
      //     ...SECTIONS.analise,
      //     nested: null,
      //     disabled: false,
      //   },
      // ],
      disabled: false
    },
    {
      ...SECTIONS.avaliacoes,
      nested: null,
      disabled: process.env.REACT_APP_ENV === 'prd'
    }
  ];

  const MENU_WIDTH = MAIN_ROUTES_WIDTH;

  function updateRoute(pPath = '/') {
    navigate(pPath);
  }

  function handleSelected(pEvent, pIndex, pCurrentOptions) {
    pEvent.stopPropagation();
    updateRoute(pCurrentOptions.path);
  }

  function handleNestedSelected(pCurrentOptions) {
    updateRoute(pCurrentOptions.path);
  }

  return (
    <Drawer
      id="sidebar"
      variant="permanent"
      sx={{
        width: MENU_WIDTH,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: MENU_WIDTH,
          boxSizing: 'border-box',
          overflow: 'hidden',
          zIndex: 1000
        }
      }}>
      <Stack direction="row" flexGrow={1}>
        <Stack
          sx={{
            width: MAIN_ROUTES_WIDTH,
            flexShrink: 0,
            borderRight: '1px solid #2e2e46',
            justifyContent: 'space-between'
          }}
          spacing={2}>
          {/* Root Menu */}
          <Stack>
            <AppBar position="static" elevation={0}>
              <Toolbar disableGutters sx={{ justifyContent: 'center' }}>
                <Link to={props.isLoggedIn ? '/dashboard' : '/'}>
                  <Icon iconName="investira_icon" size={32} color="primary" />
                </Link>
              </Toolbar>
            </AppBar>
            <List
              disablePadding
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1
              }}>
              {options.map((option, index) => {
                if (validators.isEmpty(option)) {
                  return null;
                }

                if (option.key === 'divider') {
                  return <Divider key={crypto.randomUUID()} />;
                }

                const isSelected = pathname.split('/')[1] === option.path.split('/')[1];

                return (
                  <ListItem
                    key={`${option.key}-${index}`}
                    disablePadding
                    disableGutters
                    disabled={option.disabled}
                    button
                    to={option.path}
                    // onClick={(e) => handleSelected(e, index, option)}
                    selected={isSelected}
                    sx={{ height: '32px', width: '32px', borderRadius: 1 }}
                    onMouseEnter={e => handleItemHover(e, option)}
                    onMouseLeave={handleItemLeave}>
                    {option.nested ? (
                      <IconButton
                        disableRipple
                        sx={{
                          height: '32px',
                          width: '32px'
                        }}
                        onClick={e => handleSelected(e, index, option)}
                        // onMouseEnter={(event) =>
                        //   setAnchorEl(event.currentTarget)
                        // }
                        // onMouseLeave={(event) => {
                        //   handleClose()
                        // }
                      >
                        {option.icon}
                      </IconButton>
                    ) : (
                      <Tooltip title={option.label} placement="right">
                        <IconButton
                          disableRipple
                          sx={{
                            height: '32px',
                            width: '32px'
                          }}
                          onClick={e => handleSelected(e, index, option)}>
                          {option.icon}
                        </IconButton>
                      </Tooltip>
                    )}
                  </ListItem>
                );
              })}
            </List>
            <Menu
              id="nested-menu"
              anchorEl={anchorEl}
              open={open && !!activeItem?.nested}
              onClose={() => {
                setAnchorEl(null);
                setActiveItem(null);
              }}
              PaperProps={{
                onMouseEnter: handleSubmenuEnter,
                onMouseLeave: handleSubmenuLeave,
                sx: {
                  minWidth: 250,
                  borderRadius: '8px',
                  marginLeft: '5px', // Garante sobreposição com a sidebar
                  pointerEvents: 'auto' // Importante para manter a interação
                }
              }}
              MenuListProps={{
                autoFocus: false,
                dense: true
              }}
              elevation={1}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
              disablePortal
              componentsProps={{
                backdrop: {
                  sx: {
                    backgroundColor: 'transparent', // Remove o backdrop
                    pointerEvents: 'none' // Permite interação com elementos abaixo
                  }
                }
              }}
              sx={{
                pointerEvents: 'none', // Permite que o mouse passe para o submenu
                '& .MuiPaper-root': {
                  pointerEvents: 'auto' // Reativa interação no submenu
                }
              }}>
              <ListSubheader>{activeItem?.label}</ListSubheader>
              {activeItem?.nested?.map((nestedOption, index) => (
                <MenuItem
                  key={`${nestedOption.key}-${index}`}
                  disabled={nestedOption.disabled}
                  onClick={() => handleNestedSelected(nestedOption)}
                  selected={pathname === nestedOption.path}>
                  {nestedOption.label}
                </MenuItem>
              ))}
            </Menu>
          </Stack>

          <Stack>
            <UserProfile />
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  );
};

Sidebar.displayName = 'Sidebar';

export default Sidebar;
